import { useCallback } from 'react';

import { DeliveryMethods } from '~/api/ETA';
import { company } from '~/company/Company';
import { ModalType } from '~/components/Modal/interface';
import { useModal } from '~/hooks/useModal';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import {
  cartStore,
  checkoutStore,
  deliveryMethodAvailabilityStore,
} from '~/stores';
import { getAddressModalParams } from '~/utils/addressModal';
import { usageForDeliveryMethod } from '~/utils/deliveryMethod';

interface UseDeliveryMethodSwitchOptions {
  /**
   * Route the switch through the saved-address-or-new-address gate
   * (getAddressModalParams) instead of applying it immediately. The method
   * only actually changes once the opened modal's own confirm path sets it
   * (address selection and the new-address save flow already do this, see
   * ModalAddressList and useDeliveryPickupSaveAddress) — dismissing the
   * modal without picking an address commits nothing. Only the header
   * switcher opts in; checkout's tabs keep switching immediately.
   */
  gateOnSavedAddress?: boolean;
}

/**
 * Delivery-method switch handler shared by the checkout tabs and the header
 * switcher. When the switcher feature is active and the target method is
 * served by a different warehouse than the current one, switching would
 * invalidate the cart built against the current warehouse, so the change is
 * deferred to a confirmation modal (which empties the cart on confirm).
 * Otherwise the method switches immediately, unless `gateOnSavedAddress` is
 * set, in which case it is deferred to the address-selection modal instead.
 * Tracks the selection on every direct (non-deferred, non-gated) switch.
 */
export function useDeliveryMethodSwitch(
  options?: UseDeliveryMethodSwitchOptions,
): (method: DeliveryMethods) => void {
  const { openModal } = useModal();

  return useCallback(
    (method: DeliveryMethods) => {
      if (method === checkoutStore.deliveryMethod) {
        return;
      }

      if (
        company.showDeliveryMethodSwitcher &&
        cartStore.cart.length > 0 &&
        deliveryMethodAvailabilityStore.warehousesDiffer
      ) {
        checkoutStore.setPendingDeliveryMethod(method);
        openModal(ModalType.DeliveryMethodChange);
        return;
      }

      if (options?.gateOnSavedAddress && company.showDeliveryMethodSwitcher) {
        const usage = usageForDeliveryMethod(method);
        const [targetModal, source, params] = getAddressModalParams(
          'HeaderDeliverySwitcher',
          undefined,
          usage,
        );
        openModal(targetModal, source, params);
        return;
      }

      checkoutStore.setDeliveryMethod(method);
      analyticsService.track(AnalyticsEvent.DELIVERY_METHOD_SELECTED, {
        type:
          method === DeliveryMethods.CLICK_AND_COLLECT ? 'Pickup' : 'Delivery',
      });
    },
    [openModal, options?.gateOnSavedAddress],
  );
}
